import { useState } from 'react'
import { Head, router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import {
  adminInstrumentTypesPath,
  draftPlanningAdminInstrumentTypesPath,
} from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'

interface FormErrors {
  [key: string]: string[] | undefined
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const WizardStepOne = () => {
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props
  const [cancelOpen, setCancelOpen] = useState(false)

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.instrumentTypes'), href: adminInstrumentTypesPath() },
    { label: t('admin.instrumentTypes.wizardTitle') },
  ])

  const form = useForm({
    name: '',
    manufacturerText: '',
    modelNumberText: '',
  })
  const canCreate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.CREATE)

  const errors: FormErrors = (pageErrors as FormErrors | undefined) ?? {}

  // REBUILD-39: Next advances the wizard in-memory only — no POST create until Finish.
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const name = form.data.name.trim()
    if (!name) return

    router.get(
      draftPlanningAdminInstrumentTypesPath({
        name,
        manufacturer_text: form.data.manufacturerText.trim(),
        model_number_text: form.data.modelNumberText.trim(),
      }),
    )
  }

  const confirmCancelCreate = () => {
    setCancelOpen(false)
    router.visit(adminInstrumentTypesPath())
  }

  return (
    <div className="space-y-6">
      <Head title={t('admin.instrumentTypes.wizardTitle')} />
      <div className="rounded-lg border p-6 max-w-xl">
        <p className="text-sm font-medium text-muted-foreground">
          {t('admin.instrumentTypes.wizardStepOneLabel')}
        </p>
        <h1 className="text-2xl font-bold mb-6 mt-1">{t('admin.instrumentTypes.wizardTitle')}</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('admin.instrumentTypes.nameField')} *</Label>
            <Input
              id="name"
              value={form.data.name}
              onChange={(event) => form.setData('name', event.target.value)}
              placeholder={t('admin.instrumentTypes.namePlaceholder')}
              required
              autoFocus
              autoComplete="off"
            />
            <FieldError errors={errors} field="name" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manufacturerText">
              {t('admin.instrumentTypes.manufacturerTextField')}
            </Label>
            <Input
              id="manufacturerText"
              value={form.data.manufacturerText}
              onChange={(event) => form.setData('manufacturerText', event.target.value)}
              placeholder={t('admin.instrumentTypes.manufacturerTextPlaceholder')}
              autoComplete="off"
            />
            <FieldError errors={errors} field="manufacturer_text" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="modelNumberText">
              {t('admin.instrumentTypes.modelNumberTextField')}
            </Label>
            <Input
              id="modelNumberText"
              value={form.data.modelNumberText}
              onChange={(event) => form.setData('modelNumberText', event.target.value)}
              placeholder={t('admin.instrumentTypes.modelNumberTextPlaceholder')}
              autoComplete="off"
            />
            <FieldError errors={errors} field="model_number_text" />
          </div>

          {(errors.base?.length ?? 0) > 0 && (
            <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
              {errors.base!.map((message, index) => (
                <p key={index} className="text-sm text-destructive">
                  {message}
                </p>
              ))}
            </div>
          )}

          {!canCreate && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canCreate && (
              <Button type="submit" disabled={form.processing}>
                {t('admin.instrumentTypes.wizardNext')}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setCancelOpen(true)}>
              {t('admin.instrumentTypes.cancelButton')}
            </Button>
          </div>
        </form>
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.instrumentTypes.cancelCreateDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.instrumentTypes.cancelCreateDialog.body')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>
              {t('admin.instrumentTypes.cancelCreateDialog.cancel')}
            </Button>
            <Button type="button" onClick={confirmCancelCreate}>
              {t('admin.instrumentTypes.cancelCreateDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WizardStepOne
